import { useEffect, useState } from 'react'
import { type_GM_STATE } from '../../helpers'
import { addCartItem, addMultipleCartItems } from '../helpers/cart'
import { globalEmitter } from '../helpers/emitter'
import { wishlistProviderService } from '../helpers/provider.service'
import { OmneoListItem, OmneoListItemMeta, PropsWishlistSharedDisplayState } from '../types'
import { LoaderSpin } from './LoaderSpin'
import { WishlistCard } from './WishlistCard'
import { DashboardError } from './DashboardError'
import { itemShouldBeHidden } from '../helpers/utils'
import { OmneoListItemInStore } from '../types/omneo-wishlist-item'
import { WishlistDashboard } from './WishlistDashboard'

declare var GM_STATE: type_GM_STATE

export function WishlistSharedDisplay({ showDashboardPageTitle = true }: { showDashboardPageTitle?: boolean }) {
  const [state, setState] = useState<PropsWishlistSharedDisplayState>({
    loading: true,
    items: [],
    emptyWishlist: false,
    error: false,
    errorType: null,
    name: 'Wishlist'
  })
  const [currentItem, setCurrentItem] = useState<OmneoListItemMeta | null>(null)
  const { network, invalidHandle, linkHref, linkTitle } = GM_STATE.wishlist.sharedWishlist.errorMessages
  const searchParams = new URLSearchParams(location.search)
  const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''))
  const queryHandle = searchParams.get('handle')
  const detailHashId = hashParams.get('view') === 'detail' ? hashParams.get('id') : null
  const [sharedHandle, setSharedHandle] = useState<string | null>(queryHandle)
  const [showDashboard, setShowDashboard] = useState(!queryHandle && !detailHashId)

  useEffect(() => {
    let isUnmounted = false

    if (queryHandle) {
      setSharedHandle(queryHandle)
      setShowDashboard(false)
      return
    }

    if (!detailHashId) {
      setSharedHandle(null)
      setShowDashboard(true)
      return
    }

    const resolveDetailRoute = async () => {
      const canAccessLists = wishlistProviderService.isCustomerLoggedIn() || wishlistProviderService.supportsGuestLists()

      if (canAccessLists) {
        const resolveAgainstLists = async () => {
          const lists = await wishlistProviderService.getLists()
          if (isUnmounted) {
            return
          }

          const matchingList = lists.find((list) => list.id?.toString() === detailHashId)

          if (matchingList) {
            setSharedHandle(null)
            setShowDashboard(true)
            return
          }

          setSharedHandle(detailHashId)
          setShowDashboard(false)
        }

        if (wishlistProviderService.isReady()) {
          resolveAgainstLists().then()
        } else {
          return wishlistProviderService.onReady(() => {
            resolveAgainstLists().then()
          })
        }

        return
      }

      setSharedHandle(detailHashId)
      setShowDashboard(false)
    }

    resolveDetailRoute().then()

    return () => {
      isUnmounted = true
    }
  }, [detailHashId, queryHandle])

  useEffect(() => {
    if (!sharedHandle) {
      return
    }

    const getWishlist = async () => {
      const listId = sharedHandle

      try {
        const list = listId ? await wishlistProviderService.getSharedList(listId) : null

        if (!list) {
          throw new Error('Invalid shared wishlist handle')
        }

        const listItems = (list.items || []).filter((item: OmneoListItem | OmneoListItemInStore) => !itemShouldBeHidden(item))
        const emptyWishlist = listItems.length === 0

        setState((currentState) => ({
          ...currentState,
          loading: false,
          items: listItems,
          emptyWishlist,
          error: false,
          errorType: null,
          name: list.name || currentState.name
        }))
      } catch (err) {
        console.error('Could not get shared wishlist', listId, err)
        const errorType = err instanceof Error && /invalid/i.test(err.message) ? 'invalidHandle' : 'network'
        setState((currentState) => ({
          ...currentState,
          loading: false,
          error: true,
          errorType
        }))
      }
    }

    getWishlist()
  }, [sharedHandle])

  if (showDashboard) {
    return <WishlistDashboard showListPageTitle={showDashboardPageTitle} />
  }

  if (state.error) {
    let errorMessage = network
    switch (state.errorType) {
      case 'invalidHandle':
        errorMessage = invalidHandle
        break
      case 'network':
        errorMessage = network
        break
    }
    return <DashboardError errorMessage={errorMessage} linkHref={linkHref} linkTitle={linkTitle} />
  }

  if (state.loading && !currentItem) {
    return (
      <div className="relative w-100 p-4 md:p-32 flex flex-col justify-center items-center ">
        <LoaderSpin />
      </div>
    )
  }

  const addToBag = async (item: OmneoListItem | OmneoListItemInStore) => {
    const itemMeta = item.meta

    setCurrentItem(itemMeta)
    setState({ ...state, loading: true, items: state.items, emptyWishlist: false, error: false })

    try {
      if (Array.isArray(itemMeta.properties) && itemMeta.properties.length > 0) {
        const properties = {
          items: itemMeta.properties.map((item: any) => {
            const newItem = { ...item };

            if (newItem.properties.hasOwnProperty('_uniqueTimeStamp')) {
              newItem.properties._uniqueTimeStamp = Date.now();
            }
            if (newItem.properties.hasOwnProperty('_uniqueItem')) {
              newItem.properties._uniqueItem = Date.now();
            }
            if (Array.isArray(newItem.properties) && newItem.properties.length === 0) {
              newItem.properties = {}
            }
    
            return newItem;
          })
        }

        const rest = await addMultipleCartItems(properties)
        if (rest) {
          globalEmitter.emit('product:added', { quantity: 1, variant: { name: rest.items.find((i: any) => i.id === itemMeta.variantId)?.title || rest.items[0]?.title } })
        }
      } else {
        const properties = {
          _uniqueTimeStamp: Date.now()
        }
        const res = await addCartItem(itemMeta.variantId, properties)
        if (res) {
          globalEmitter.emit('product:added', { quantity: 1, variant: { name: res.items[0]?.title } })
        }
      }
    } catch (e) {
      console.error('error', e)
      globalEmitter.emit('triggerNotification', { message: 'Add item to cart failed', type: 'error' })
    }
    setCurrentItem(null)
    setState({ ...state, loading: false, items: state.items, emptyWishlist: false, error: false })
  }

  return (
    <div className="grid grid-cols-2 gap-x-2.5 gap-y-6 md:gap-x-5 md:grid-cols-3 lg:grid-cols-4 md-max:-mx-3 md-max:-my-1 px-5">
      { (state.items.length === 0 || state.emptyWishlist) ?
        (
          <div className="w-full h-full flex flex-col justify-center items-center">No items to share</div>
        ):(
          state.items
            .filter((item: OmneoListItem | OmneoListItemInStore) => !item.meta.hidden)
            .map((item: OmneoListItem | OmneoListItemInStore, index) => (
              <WishlistCard
                key={item.id}
                item={item}
                addToBag={addToBag}
                loading={state.loading}
                currentVariantId={currentItem?.variantId || null}
                allowRemoveFromList={false}
                wishlistType='shared'
                index={index}
                currentIndex={null}
              />
            )))
        }
    </div>
  )
}
